import { createConsoleExporter, createNoopExporter, createOtlpExporter, randomHex } from './exporters';
import { type CreateTelemetryOptions, type Span, type Telemetry, type TelemetryAttributes, type TelemetryExporter, type TelemetryItem } from './types';
import { type ZagruRuntimeConfig } from '../config/runtime-config';

function nowMs(): number {
  return typeof performance !== 'undefined' && typeof performance.timeOrigin === 'number' ? performance.timeOrigin + performance.now() : Date.now();
}

function errorAttributes(error: unknown): TelemetryAttributes {
  if (error instanceof Error) return { 'exception.type': error.name, 'exception.message': error.message };
  if (error === undefined) return {};
  return { 'exception.message': String(error) };
}

/**
 * Lightweight tracer/meter: batches spans, events and metrics, flushes on an interval, when the
 * batch is full, when the page hides (`visibilitychange`/`pagehide`) and on `shutdown()`.
 */
export function createTelemetry(options: CreateTelemetryOptions): Telemetry {
  const exporter: TelemetryExporter = options.exporter === 'console' ? createConsoleExporter() : options.exporter === 'none' ? createNoopExporter() : options.exporter;
  const maxBatch = options.maxBatch ?? 50;
  const flushIntervalMs = options.flushIntervalMs ?? 5000;
  const sampleRate = options.sampleRate ?? 1;
  let globalAttributes: TelemetryAttributes = { ...(options.serviceName ? { 'service.name': options.serviceName } : {}), ...(options.globalAttributes ?? {}) };
  let queue: TelemetryItem[] = [];
  let timer: ReturnType<typeof setInterval> | null = null;
  let stopped = false;
  let inflight: Promise<void> = Promise.resolve();

  const merge = (attrs?: TelemetryAttributes): TelemetryAttributes => ({ ...globalAttributes, ...(attrs ?? {}) });

  async function flush(): Promise<void> {
    if (queue.length === 0) return inflight;
    const batch = queue;
    queue = [];
    const run = (async () => {
      try {
        await exporter.export(batch);
      } catch (err) {
        console.warn('[zagru/platform] telemetry export failed', err);
      }
    })();
    inflight = inflight.then(() => run, () => run);
    return inflight;
  }

  function enqueue(item: TelemetryItem): void {
    if (stopped) return;
    queue.push(item);
    if (queue.length >= maxBatch) void flush();
  }

  function ensureTimer(): void {
    if (timer !== null || stopped || flushIntervalMs <= 0) return;
    timer = setInterval(() => void flush(), flushIntervalMs);
  }

  const sampledTraces = new Map<string, boolean>();

  function startSpan(name: string, attrs?: TelemetryAttributes, parent?: Span): Span {
    ensureTimer();
    const traceId = parent ? parent.traceId : randomHex(32);
    const spanId = randomHex(16);
    let sampled: boolean;
    if (parent) {
      sampled = sampledTraces.get(traceId) ?? true;
    } else {
      sampled = sampleRate >= 1 || Math.random() < sampleRate;
      sampledTraces.set(traceId, sampled);
      if (sampledTraces.size > 500) sampledTraces.delete(sampledTraces.keys().next().value as string);
    }
    const start = nowMs();
    const attributes: TelemetryAttributes = { ...(attrs ?? {}) };
    const events: Array<{ name: string; time: number; attributes?: TelemetryAttributes }> = [];
    let ended = false;
    return {
      name,
      traceId,
      spanId,
      setAttribute: (k, v) => {
        attributes[k] = v;
      },
      addEvent: (eventName, eventAttrs) => {
        events.push(eventAttrs ? { name: eventName, time: nowMs(), attributes: eventAttrs } : { name: eventName, time: nowMs() });
      },
      end: (status = 'ok', error) => {
        if (ended) return;
        ended = true;
        if (!sampled) return;
        const item: TelemetryItem = {
          kind: 'span',
          name,
          traceId,
          spanId,
          start,
          end: nowMs(),
          status,
          attributes: merge({ ...attributes, ...errorAttributes(error) }),
          events,
        };
        if (parent) item.parentSpanId = parent.spanId;
        enqueue(item);
      },
    };
  }

  function onHide(): void {
    if (typeof document === 'undefined' || document.visibilityState === 'hidden') void flush();
  }
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onHide);
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', onHide);
  }

  return {
    startSpan,
    event: (name, attrs) => {
      ensureTimer();
      enqueue({ kind: 'event', name, time: nowMs(), attributes: merge(attrs) });
    },
    metric: (name, value, attrs) => {
      ensureTimer();
      enqueue({ kind: 'metric', name, value, time: nowMs(), attributes: merge(attrs) });
    },
    withSpan: async (name, fn, attrs) => {
      const span = startSpan(name, attrs);
      try {
        const result = await fn(span);
        span.end('ok');
        return result;
      } catch (err) {
        span.end('error', err);
        throw err;
      }
    },
    flush,
    shutdown: async () => {
      stopped = true;
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onHide);
      if (typeof window !== 'undefined') window.removeEventListener('pagehide', onHide);
      await flush();
      await exporter.shutdown?.();
    },
    setGlobalAttributes: (attrs) => {
      globalAttributes = { ...globalAttributes, ...attrs };
    },
  };
}

/** Builds a Telemetry instance from the runtime `telemetry` configuration section. */
export function telemetryFromConfig(config?: ZagruRuntimeConfig['telemetry']): Telemetry {
  const exporterKind = config?.exporter ?? 'none';
  const serviceName = config?.serviceName ?? 'zagru-web';
  const base = { serviceName, ...(config?.sampleRate !== undefined ? { sampleRate: config.sampleRate } : {}) };
  if (exporterKind === 'otlp' && config?.otlpUrl) {
    return createTelemetry({ ...base, exporter: createOtlpExporter({ url: config.otlpUrl, serviceName }) });
  }
  if (exporterKind === 'console') return createTelemetry({ ...base, exporter: 'console' });
  return createTelemetry({ ...base, exporter: 'none' });
}
